import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Separator } from '@/components/ui/separator';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { NavigationProps } from '@/types/navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { PieChart, BarChart3, TrendingUp, TrendingDown, DollarSign, Activity, Wallet, Loader2, RefreshCcw, Settings, Plus, PiggyBank, GripVertical } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { usePlaid } from '@/contexts/PlaidContext';
import { useAuth } from '@/contexts/AuthContext';
import { db } from '@/firebase';
import { collection, query, where, getDocs, orderBy, limit, startAfter, QueryDocumentSnapshot, getCountFromServer } from 'firebase/firestore';
import { Button } from '@/components/ui/button';
import { PlaidLinkButton } from '@/components/PlaidLinkButton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Transaction, RecurringTransaction } from '@/types/transaction'; // Import both types
import { format } from 'date-fns'; // Keep format if used elsewhere, remove others
import { getFunctions, httpsCallable } from 'firebase/functions'; // Import Firebase functions
import { HorizontalBarChart } from '@/components/charts/HorizontalBarChart';
import { RecentTransactionsCard } from '@/components/dashboard/RecentTransactionsCard'; // <-- Import the new component
import { UpcomingTransactionsCard } from '@/components/dashboard/UpcomingTransactionsCard'; // <-- Import the new component
import { AIChatCard } from '@/components/dashboard/AIChatCard'; // <-- Import the AI chat card
import { EarnedBadgesCard, BadgeInfo } from '@/components/dashboard/EarnedBadgesCard'; // <-- Import the new component
import { normalizeCategory } from '@/lib/utils'; // Import normalizeCategory, Removed formatCurrency import
import { BankAccount } from '@/types/plaid'; // Import BankAccount type
import { Calendar } from "@/components/ui/calendar";
import { Progress } from "@/components/ui/progress"; // <-- Import Progress component
import { PlaidItem } from '@/types/plaid'; // Ensure this is imported
import {
    DndContext,
    closestCenter, // Consider rectIntersection if needed
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    rectIntersection, // Using rectIntersection for grid layout
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy, // Consider rectSortingStrategy for grid
    rectSortingStrategy, // Using rectSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  mockTransactions, 
  mockRecurringTransactions, 
  mockBankAccounts,
  mockPlaidItems,
  dashboardBudgetData
} from '@/lib/mockData';

// Define UserCategorySetting interface locally (same as in RecentTransactionsCard)
interface UserCategorySetting {
  name: string;
  emoji: string;
  is_active: true
}

// Define basic types for investment data (replace with more specific types if available)
// Based on Plaid API structure
interface InvestmentAccount {
  account_id: string;
  balances: { current: number | null; available: number | null; iso_currency_code: string | null };
  mask: string | null;
  name: string;
  official_name: string | null;
  type: string; // 'investment'
  subtype: string | null;
}

interface Security {
  security_id: string;
  name: string | null;
  ticker_symbol: string | null;
  close_price: number | null;
  iso_currency_code: string | null;
  // ... other security fields
}

interface Holding {
  account_id: string;
  security_id: string;
  quantity: number;
  institution_value: number;
  cost_basis: number | null;
  iso_currency_code: string | null;
  // ... other holding fields
}

// interface InvestmentTransaction { ... } // Define if needed later

interface DashboardPageProps extends NavigationProps {}

const TRANSACTIONS_PAGE_SIZE = 20; // Define page size

// Define type for aggregated spending data
interface SpendingSummaryItem {
  category: string;
  total: number;
}

// Local type for mock data including logo
interface MockPlaidItem extends PlaidItem {
  logoUrl?: string;
}

// Helper to format currency (Moved from budget page)
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

// Placeholder budget data (replace with actual data fetching)
interface BudgetCategoryData {
  id: string;
  name: string;
  icon: string;
  allocated: number;
  spent: number;
  color?: string;
}

// --- Badge Levels (Copied from PlacesPage) ---
const badgeLevels = [
  { id: 'newbie', name: 'Newbie', points: 0, icon: '🌱', description: 'Started your local spending journey!' },
  { id: 'explorer', name: 'Local Explorer', points: 500, icon: '🌳', description: 'Spent over $500 at local places.' },
  { id: 'legend', name: 'Local Legend', points: 1000, icon: '🏆', description: 'Spent over $1000 at local places.' },
];
// --- End Badge Levels ---

// --- New Financial Badge Definitions ---
// Note: These don't use 'points' like the local spending ones, but a boolean 'earned' status calculated later.
const financialBadges = [
  { id: 'account-connector-1', name: 'Account Connector I', icon: '🔗', description: 'Connected your first bank account.' },
  { id: 'account-connector-2', name: 'Account Connector II', icon: '🔗🔗', description: 'Connected 3+ bank accounts.' },
  { id: 'budget-planner', name: 'Budget Planner', icon: '✏️', description: 'Created your first budget category.' },
  // --- Add more badge definitions here later ---
  // { id: 'budget-tracker', name: 'Budget Tracker', icon: '🎯', description: 'Stayed under budget overall for one full month.' },
  // { id: 'savings-starter', name: 'Savings Starter', icon: '🌱🏦', description: 'First deposit into a linked savings account.' },
  // { id: 'investment-initiator', name: 'Investment Initiator', icon: '📈', description: 'Linked first investment account.' },
];
// --- End New Financial Badge Definitions ---

// Define IDs for the sortable dashboard card sections
const CARD_IDS = {
    // Top Summary Cards
    SUMMARY_CASH: 'summaryCash',
    SUMMARY_DEBT: 'summaryDebt',
    SUMMARY_SAVINGS: 'summarySavings',
    SUMMARY_ACCOUNTS: 'summaryAccounts',
    // Main Grid Cards
    BANK_ACCOUNTS: 'bankAccounts',
    BUDGET_PROGRESS: 'budgetProgress', // Was Top Spending Categories
    EARNED_BADGES: 'earnedBadges',
    AI_CHAT: 'aiChat',
    RECENT_TRANSACTIONS: 'recentTransactions',
    UPCOMING_TRANSACTIONS: 'upcomingTransactions',
};

// Sortable Item Wrapper Component (Copied from Budgets Overview)
function SortableItem({ id, children }: { id: string; children: React.ReactNode }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.8 : 1,
    // Ensure the item takes up grid space even while dragging
    gridColumn: isDragging ? 'span 1 / span 1' : undefined,
    gridRow: isDragging ? 'auto' : undefined,
    // width: '100%', // Ensure it fills its grid area
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group"> {/* Removed w-full */}
      {/* Drag Handle: Positioned top-left */}
      <button
        {...attributes}
        {...listeners}
        aria-label="Drag handle"
        className="absolute top-2 left-2 p-1 bg-gray-200 dark:bg-gray-700 rounded text-gray-500 dark:text-gray-400 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity cursor-grab focus:cursor-grabbing z-20"
      >
        <GripVertical size={16} />
      </button>
      {/* Make content non-interactive during drag */}
      <div style={{ pointerEvents: isDragging ? 'none' : undefined }}>
        {children}
      </div>
    </div>
  );
}

export default function DashboardPage({ onNavigate }: DashboardPageProps) {
  // const { bankAccounts, plaidItems, disconnectAccount, refreshAccounts, isLoading, syncTransactions } = usePlaid(); // <-- Comment out original hook usage
  // Use mock data instead:
  // Keep only used functions/state from usePlaid - Now empty as we use mock data only
  // const { disconnectAccount, refreshAccounts } = usePlaid(); // REMOVED as unused
  const bankAccounts = mockBankAccounts; // <-- Use mock accounts
  const plaidItems = mockPlaidItems; // <-- Use mock items (no need for MockPlaidItem type here anymore)
  const isLoading = false; // <-- Set loading to false for mock data

  const { currentUser } = useAuth();
  const [disconnectingAccounts, setDisconnectingAccounts] = useState<Record<string, boolean>>({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>(mockTransactions); 
  const [isTransactionsLoading, setIsTransactionsLoading] = useState(false); // <-- Set loading to false
  const [investmentAccounts, setInvestmentAccounts] = useState<InvestmentAccount[]>([]);
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [securities, setSecurities] = useState<Security[]>([]);
  const [isInvestmentLoading, setIsInvestmentLoading] = useState(true);
  const [investmentError, setInvestmentError] = useState<string | null>(null);
  const [spendingSummary, setSpendingSummary] = useState<SpendingSummaryItem[]>([]);
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [upcomingTransactions, setUpcomingTransactions] = useState<RecurringTransaction[]>([]);
  const [isUpcomingLoading, setIsUpcomingLoading] = useState(true);
  const [upcomingError, setUpcomingError] = useState<string | null>(null);
  const [userCategorySettings, setUserCategorySettings] = useState(new Map<string, UserCategorySetting>());
  const [isCategorySettingsLoading, setIsCategorySettingsLoading] = useState(true);
  const [isBudgetsLoading, setIsBudgetsLoading] = useState(false); 

  const functions = getFunctions(); // Initialize Firebase Functions

  // State to manage the order of the draggable cards
  const [topCardOrder, setTopCardOrder] = useState<string[]>([
    CARD_IDS.SUMMARY_CASH,
    CARD_IDS.SUMMARY_DEBT,
    CARD_IDS.SUMMARY_SAVINGS,
    CARD_IDS.SUMMARY_ACCOUNTS,
  ]);
  const [mainCardOrder, setMainCardOrder] = useState<string[]>([
    CARD_IDS.BANK_ACCOUNTS,
    CARD_IDS.BUDGET_PROGRESS,
    CARD_IDS.EARNED_BADGES,
    CARD_IDS.AI_CHAT,
    CARD_IDS.RECENT_TRANSACTIONS,
    CARD_IDS.UPCOMING_TRANSACTIONS,
  ]);

  // Sensors for dnd-kit
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // --- Fetch Investment Holdings --- 
  const fetchInvestmentHoldings = useCallback(async () => {
    if (!currentUser || plaidItems.length === 0) {
      console.log("No user or Plaid items, skipping investment fetch.");
      setIsInvestmentLoading(false);
      setInvestmentAccounts([]);
      setHoldings([]);
      setSecurities([]);
      return;
    }

    console.log("Fetching investment holdings...");
    setIsInvestmentLoading(true);
    setInvestmentError(null);

    let allAccounts: InvestmentAccount[] = [];
    let allHoldings: Holding[] = [];
    let allSecurities: Security[] = [];
    const processedSecurityIds = new Set<string>();

    try {
      const getHoldingsFn = httpsCallable(functions, 'getInvestmentHoldings');
      
      // Fetch for each plaid item that might have investment products
      // Note: We don't know which item has which product after linking,
      // so we might need to call this for all transaction-based items
      // or filter plaidItems if we stored product info during exchangeToken.
      // For now, let's assume all items *could* have investments if the product was requested.
      const holdingPromises = plaidItems.map(item => 
        getHoldingsFn({ plaidItemId: item.plaidItemId }).catch(err => {
          console.error(`Error fetching holdings for item ${item.plaidItemId}:`, err);
          // Allow other fetches to proceed, maybe set a specific error state per item if needed
          if (err.code === 'failed-precondition' && err.details?.needsRelink) {
            // Handle relink needed state if necessary (e.g., update item state in PlaidContext)
             setInvestmentError(`Account ${item.institutionName} needs relinking.`);
          }
          return null; // Return null on error for this specific item
        })
      );

      const results = await Promise.all(holdingPromises);

      results.forEach(result => {
        if (result && result.data) {
          const data = result.data as { accounts: InvestmentAccount[], holdings: Holding[], securities: Security[] };
          // Filter for actual investment accounts from the response
          const itemInvestmentAccounts = data.accounts?.filter(acc => acc.type === 'investment') || [];
          allAccounts = allAccounts.concat(itemInvestmentAccounts);
          allHoldings = allHoldings.concat(data.holdings || []);
          
          // Deduplicate securities across items
          (data.securities || []).forEach(sec => {
            if (!processedSecurityIds.has(sec.security_id)) {
              allSecurities.push(sec);
              processedSecurityIds.add(sec.security_id);
            }
          });
        }
      });

      setInvestmentAccounts(allAccounts);
      setHoldings(allHoldings);
      setSecurities(allSecurities);
      console.log(`Fetched investments: ${allAccounts.length} accounts, ${allHoldings.length} holdings, ${allSecurities.length} unique securities.`);

    } catch (error: any) {
      console.error("General error fetching investment holdings:", error);
      setInvestmentError("Failed to load investment data.");
      // Clear state on general error
      setInvestmentAccounts([]);
      setHoldings([]);
      setSecurities([]);
    } finally {
      setIsInvestmentLoading(false);
      console.log("Finished fetching investment holdings.");
    }
  }, [currentUser, plaidItems, functions]); // Include functions in dependencies
  // --- End Fetch Investment Holdings ---

  // --- Fetch User Category Settings ---
  const fetchUserCategorySettings = useCallback(async () => {
    if (!currentUser || !db) {
      console.log("User or DB not available, skipping category settings fetch.");
      setUserCategorySettings(new Map());
      setIsCategorySettingsLoading(false);
      return;
    }
    console.log("Dashboard: Fetching user category settings...");
    setIsCategorySettingsLoading(true);
    const settingsMap = new Map<string, UserCategorySetting>();
    try {
      const categoriesCol = collection(db, 'userCategories');
      const q = query(categoriesCol, where("userId", "==", currentUser.uid));
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        settingsMap.set(doc.id, { 
          name: data.name || normalizeCategory(doc.id), 
          emoji: data.emoji || '🤷',
          is_active: true
        });
      });
      console.log(`Dashboard: Fetched ${settingsMap.size} user category settings.`);
      setUserCategorySettings(settingsMap);
    } catch (error) {
      console.error("Dashboard: Error fetching user category settings:", error);
      setUserCategorySettings(new Map());
    } finally {
      setIsCategorySettingsLoading(false);
    }
  }, [currentUser]);
  // --- End Fetch User Category Settings ---

  // --- Fetch Spending Summary (Backend Aggregation) --- 
  const fetchSpendingSummary = useCallback(async () => {
    if (!currentUser) {
      console.log("No user, skipping spending summary fetch.");
      setSpendingSummary([]);
      setIsSummaryLoading(false);
      setSummaryError(null);
      return;
    }

    console.log("Fetching spending summary...");
    setIsSummaryLoading(true);
    setSummaryError(null);

    try {
      const getSpendingSummaryFn = httpsCallable(functions, 'getSpendingSummary');
      const result = await getSpendingSummaryFn(); // Assumes function takes no args (gets user from context)
      // Assuming the backend function returns { data: SpendingSummaryItem[] }
      const summaryData = result.data as SpendingSummaryItem[]; 

      if (Array.isArray(summaryData)) {
          setSpendingSummary(summaryData);
          console.log(`Fetched spending summary: ${summaryData.length} categories.`);
      } else {
          console.error("Invalid data format received from getSpendingSummary:", summaryData);
          throw new Error("Invalid data format from backend.")
      }

    } catch (error: any) {
      console.error("Error fetching spending summary:", error);
      setSpendingSummary([]);
      setSummaryError("Failed to load spending summary.");
    } finally {
      setIsSummaryLoading(false);
      console.log("Finished fetching spending summary.");
    }
  }, [currentUser, functions]);
  // --- End Fetch Spending Summary ---

  // --- Fetch Upcoming Transactions (Placeholder) ---
  const fetchUpcomingTransactions = useCallback(async () => {
      // --- START MOCK IMPLEMENTATION ---
      console.log("Dashboard: Using MOCK upcoming transaction data.");
      setIsUpcomingLoading(true); // Still show loading briefly
      setUpcomingError(null);
  
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 500));
  
      // Filter mock data (only active, sort by predicted date) - Use imported mock data
      const activeSortedStreams = mockRecurringTransactions
        .filter(stream => stream.is_active && stream.predicted_next_date)
        .sort((a, b) => a.predicted_next_date.localeCompare(b.predicted_next_date));
  
      setUpcomingTransactions(activeSortedStreams);
      setIsUpcomingLoading(false);
      console.log(`Dashboard: Mock upcoming data loaded: ${activeSortedStreams.length} active streams.`);
      // --- END MOCK IMPLEMENTATION ---

      /* --- ORIGINAL IMPLEMENTATION (Commented out) ---
      if (!currentUser) {
          console.log("No user, skipping upcoming transactions fetch.");
          setUpcomingTransactions([]);
          setIsUpcomingLoading(false);
          setUpcomingError(null);
          return;
      }

      console.log("Fetching upcoming transactions via Cloud Function...");
      setIsUpcomingLoading(true);
      setUpcomingError(null);
      
      try {
        const getRecurringFn = httpsCallable(functions, 'getRecurringTransactions');
        const result = await getRecurringFn(); // Call the function
        // Assuming the function returns { data: { inflow_streams: [], outflow_streams: [] } }
        const data = result.data as { inflow_streams: RecurringTransaction[], outflow_streams: RecurringTransaction[] };
        
        // Combine inflow and outflow streams
        // We primarily care about outflows for upcoming bills, but could show inflows too
        const allStreams = [...(data.inflow_streams || []), ...(data.outflow_streams || [])];
        
        // Filter for active streams and sort by next_date ascending
        const activeSortedStreams = allStreams
            // .filter(stream => stream.status === 'ACTIVE' && stream.next_date) // Ensure next_date exists
            .filter(stream => stream.is_active && stream.predicted_next_date) // Use is_active and predicted_next_date
            .sort((a, b) => a.predicted_next_date.localeCompare(b.predicted_next_date)); // Sort by predicted_next_date

        setUpcomingTransactions(activeSortedStreams);
        console.log(`Fetched ${activeSortedStreams.length} active upcoming transactions.`);
          
      } catch (error: any) { // Explicitly type error
          console.error("Error fetching upcoming transactions:", error);
          // Provide more context if it's an HttpsError
          if (error.code && error.message) {
              console.error(`Cloud Function Error (${error.code}): ${error.message}`, error.details);
              setUpcomingError(`Failed to load upcoming transactions (${error.code}).`);
          } else {
              setUpcomingError("Failed to load upcoming transactions.");
          }
          setUpcomingTransactions([]);
      } finally {
          setIsUpcomingLoading(false);
          console.log("Finished fetching upcoming transactions.");
      }
      --- END ORIGINAL IMPLEMENTATION --- */
  }, [currentUser]); // Only currentUser dependency for mock
  // --- End Fetch Upcoming Transactions ---

  // --- Calculate Local Spending Points (Based on PlacesPage logic) ---
  const localSpendingPoints = useMemo(() => {
    // Using the same mockTransactions as the rest of the dashboard
    return mockTransactions
      .filter(tx => tx.location?.lat != null && tx.location?.lon != null && tx.amount < 0) // Negative amount = spending
      .reduce((sum, tx) => sum + Math.abs(tx.amount), 0);
  }, []); // Depends only on mockTransactions (which don't change in this setup)
  // --- End Local Spending Points ---

  // --- Define Badge Progressions ---
  const badgeProgressions: { [key: string]: string[] } = {
    localSpending: ['newbie', 'explorer', 'legend'],
    accountConnector: ['account-connector-1', 'account-connector-2'],
    // Add more progressions here as needed
    // budgetMastery: ['budget-planner', 'budget-tracker', 'budget-pilot', 'budget-captain', 'budget-guru'],
  };
  // --- End Badge Progressions ---

  // --- Determine Earned Badges ---
  const allEarnedBadges: BadgeInfo[] = useMemo(() => {
    // Calculate local spending badges
    const localBadges = badgeLevels.map(level => ({
      ...level,
      earned: localSpendingPoints >= level.points,
    }));

    // Calculate financial badges (based on current state/mock data)
    const calculatedFinancialBadges = financialBadges.map(badge => {
      let earned = false;
      switch (badge.id) {
        case 'account-connector-1':
          earned = bankAccounts.length >= 1;
          break;
        case 'account-connector-2':
          earned = bankAccounts.length >= 3;
          break;
        case 'budget-planner':
          // Assuming if dashboardBudgetData has items, a budget exists
          // In a real scenario, check if the user has *created* any budgets.
          earned = dashboardBudgetData.length > 0; 
          break;
        // --- Add logic for other financial badges here ---
        // case 'budget-tracker': // Needs historical budget data
        //   // earned = checkHistoricalBudgetPerformance(budgetHistory, 1);
        //   break;
        // case 'savings-starter': // Needs transaction data inspection
        //   // earned = checkSavingsDeposit(transactions);
        //   break;
         // case 'investment-initiator': // Needs investment account data
         //   earned = investmentAccounts.length > 0; // Simple check if any investment accounts exist
         //   break;
        default:
          earned = false; // Default to not earned if logic isn't implemented
      }
      return { ...badge, earned };
    });

    // Combine all potentially earned badges
    const potentiallyEarned = [...localBadges, ...calculatedFinancialBadges];

    // Filter out lower-tier badges if a higher tier in the same progression is earned
    const finalEarnedBadges = potentiallyEarned.filter(badge => {
      if (!badge.earned) return false; // Only consider earned badges

      // Find which progression this badge belongs to, if any
      let progressionKey: string | null = null;
      for (const key in badgeProgressions) {
        if (badgeProgressions[key].includes(badge.id)) {
          progressionKey = key;
          break;
        }
      }

      // If the badge is part of a progression
      if (progressionKey) {
        const progression = badgeProgressions[progressionKey];
        const badgeIndex = progression.indexOf(badge.id);

        // Check if any higher tier badge in the same progression is also earned
        for (let i = badgeIndex + 1; i < progression.length; i++) {
          const higherTierBadgeId = progression[i];
          const higherTierBadge = potentiallyEarned.find(b => b.id === higherTierBadgeId);
          if (higherTierBadge && higherTierBadge.earned) {
            return false; // Don't include this lower-tier badge
          }
        }
      }

      return true; // Include this badge (either not in a progression or the highest earned in its progression)
    });

    return finalEarnedBadges;

  }, [localSpendingPoints, bankAccounts, investmentAccounts]); // Add bankAccounts and investmentAccounts as dependencies
  // --- End Earned Badges ---

  // --- Effect for Initial Load & User Change ---
  useEffect(() => {
    if (currentUser) {
      // Trigger account refresh, but don't await or depend on the function reference
      // refreshAccounts(); // No need to refresh mock data
      fetchSpendingSummary(); // <<< ADD THIS CALL
      fetchUserCategorySettings(); // Fetch category settings
      fetchUpcomingTransactions(); // Fetch upcoming transactions
    } else {
       // Clear all data on logout
       setTransactions(mockTransactions); 
       setIsTransactionsLoading(false); // <-- Set loading to false
       // Removed Firebase states reset:
       // setIsLoadingMore(false);
       // setHasMoreTransactions(false);
       // setLastVisibleTransaction(null);
       setInvestmentAccounts([]);
       setHoldings([]);
       setSecurities([]);
       setIsInvestmentLoading(true);
       setInvestmentError(null);
       setSpendingSummary([]); // <<< ADD THIS RESET
       setIsSummaryLoading(true);
       setSummaryError(null);
       setUpcomingTransactions([]); // Clear upcoming transactions
       setIsUpcomingLoading(true);
       setUpcomingError(null);
    }
    // This effect should primarily run when the user logs in or out.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]); // Only currentUser as dependency
  // --- End Initial Load Effect ---

  // --- Effect for Fetching Investments based on PlaidItems --- REMOVED - Not needed for mock
  /*
  useEffect(() => {
    // Fetch investments only when user is logged in AND plaidItems are loaded/available
    if (currentUser && plaidItems.length > 0) {
      // fetchInvestmentHoldings(); // <<< TEMPORARILY COMMENTED OUT
      // Removed fetchTransactions(false); 
    } else if (!currentUser) {
        // Explicitly clear investment data if user logs out or plaidItems become empty post-login
        // (Safety net in case the currentUser effect doesn't clear it fast enough)
        setInvestmentAccounts([]);
        setHoldings([]);
        setSecurities([]);
        setIsInvestmentLoading(true); // Reset loading for next potential fetch
        setInvestmentError(null);
    }
    // Dependencies: We ONLY want this effect to re-run if the user or the items change.
    // The fetch functions are actions to take *based* on those changes, not triggers themselves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, plaidItems]); // <<< REMOVED fetchInvestmentHoldings from dependencies
  */
  // --- End Investment Fetch Effect --- REMOVED

  // Calculate Cash Balance (Checking Accounts only)
  const totalBalance = bankAccounts
    .filter(account => account.type === 'depository' && account.name.toLowerCase().includes('checking')) // Filter for checking
    .reduce((sum, account) => sum + (account.balance || 0), 0);

  const totalDebt = bankAccounts
    .filter(account => account.type === 'credit' || account.type === 'loan')
    .reduce((sum, account) => sum + Math.abs(account.balance || 0), 0); // Use Math.abs for debt

  // Calculate Total Savings
  const totalSavings = bankAccounts
    .filter(account => account.type === 'depository' && account.name.toLowerCase().includes('savings')) // Assuming 'savings' in name indicates savings account
    .reduce((sum, account) => sum + (account.balance || 0), 0);

  // Update summary cards or add a new one
  const summaryCards = [
    {
      title: "Cash Balance",
      value: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalBalance),
      description: "Combined balance of checking accounts",
      icon: <DollarSign className="h-5 w-5 text-muted-foreground" />,
      trend: "neutral"
    },
    {
      title: "Total Debt",
      value: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalDebt),
      description: "Combined balance of credit cards & loans",
      icon: <TrendingDown className="h-5 w-5 text-muted-foreground" />,
      trend: "neutral"
    },
    {
      title: "Total Savings", 
      value: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalSavings), 
      description: "Combined balance of savings accounts", 
      icon: <PiggyBank className="h-5 w-5 text-muted-foreground" />, 
      trend: "neutral"
    },
    {
      title: "Connected Accounts",
      value: isLoading ? <Skeleton className="h-6 w-10 inline-block" /> : bankAccounts.length.toString(),
      description: "Total number of connected accounts",
      icon: <Wallet className="h-5 w-5 text-muted-foreground" />,
      trend: "neutral"
    }
  ];

  // Mock disconnect handler
  const handleDisconnect = async (plaidItemId: string, accountId: string) => {
    setDisconnectingAccounts(prev => ({ ...prev, [accountId]: true }));
    console.log(`Mock disconnect: ${plaidItemId} / ${accountId}`);
    await new Promise(resolve => setTimeout(resolve, 500)); // Simulate delay
    setDisconnectingAccounts(prev => ({ ...prev, [accountId]: false }));
  };

  // Mock refresh handler
  const handleRefreshBalances = async () => {
    setIsRefreshing(true);
    console.log('Mock refresh balances');
    await new Promise(resolve => setTimeout(resolve, 500)); // Simulate delay
    setIsRefreshing(false);
  };

  // const handleSync = async (plaidItemId: string) => { // Removed sync handler
  //   try {
  //     setIsSyncing(prev => ({ ...prev, [plaidItemId]: true }));
  //     // await syncTransactions(plaidItemId); // Keep mock logic or comment out real call
  //     console.log(`Mock sync: ${plaidItemId}`);
  //      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate delay
  //   } catch (error) {
  //     console.error('Error syncing transactions:', error);
  //   } finally {
  //     setIsSyncing(prev => ({ ...prev, [plaidItemId]: false }));
  //   }
  // };

  // Drag End Handler for main grid cards
  function handleMainGridDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setMainCardOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        // Ensure both indices are valid before attempting move
        if (oldIndex > -1 && newIndex > -1) {
          return arrayMove(items, oldIndex, newIndex);
        }
        return items; // Return original items if indices are invalid
      });
    }
  }

  // Drag End Handler for top summary cards
  function handleTopCardDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setTopCardOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        if (oldIndex > -1 && newIndex > -1) {
          return arrayMove(items, oldIndex, newIndex);
        }
        return items;
      });
    }
  }

  // Memoize card components to avoid unnecessary re-renders during drag
  const cardComponents = useMemo(() => {
    // Generate top summary card data first
    const topSummaryCardsData = [
      {
        id: CARD_IDS.SUMMARY_CASH,
        title: "Cash Balance",
        value: formatCurrency(totalBalance),
        description: "Combined balance of checking accounts",
        icon: <DollarSign className="h-5 w-5 text-muted-foreground" />,
        trend: "neutral"
      },
      {
        id: CARD_IDS.SUMMARY_DEBT,
        title: "Total Debt",
        value: formatCurrency(totalDebt),
        description: "Combined balance of credit cards & loans",
        icon: <TrendingDown className="h-5 w-5 text-muted-foreground" />,
        trend: "neutral"
      },
      {
        id: CARD_IDS.SUMMARY_SAVINGS,
        title: "Total Savings",
        value: formatCurrency(totalSavings),
        description: "Combined balance of savings accounts",
        icon: <PiggyBank className="h-5 w-5 text-muted-foreground" />,
        trend: "neutral"
      },
      {
        id: CARD_IDS.SUMMARY_ACCOUNTS,
        title: "Connected Accounts",
        value: isLoading ? <Skeleton className="h-6 w-10 inline-block" /> : bankAccounts.length.toString(),
        description: "Total number of connected accounts",
        icon: <Wallet className="h-5 w-5 text-muted-foreground" />,
        trend: "neutral"
      }
    ];

    // Map data to components for the card map
    const topSummaryComponents = topSummaryCardsData.reduce((acc, card) => {
      acc[card.id] = (
        <Card key={card.id}> {/* Add key here */}
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
            {card.icon}
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{card.value}</div>
            <p className={`text-xs ${card.trend === 'up' ? 'text-green-500' : card.trend === 'down' ? 'text-red-500' : 'text-muted-foreground'}`}>
              {card.description}
            </p>
          </CardContent>
        </Card>
      );
      return acc;
    }, {} as Record<string, JSX.Element>);

    // Return combined map with top summary and main grid components
    return {
      ...topSummaryComponents,
      [CARD_IDS.BANK_ACCOUNTS]: (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Bank Accounts</CardTitle>
                <CardDescription>Manage your connected bank accounts</CardDescription>
              </div>
              {bankAccounts.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleRefreshBalances}
                  disabled={isRefreshing}
                >
                  {isRefreshing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <RefreshCcw className="mr-2 h-4 w-4" />
                      Refresh Balances
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <PlaidLinkButton />
            
            <div className="space-y-4">
              <h4 className="text-sm font-medium">Connected Accounts</h4>
              <div className="space-y-1">
                {bankAccounts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No connected accounts</p>
                ) : (
                  bankAccounts.map((account) => {
                    const plaidItem = plaidItems.find(item => {
                      return item.plaidItemId === account.plaidItemId;
                    });
                    
                    if (!plaidItem) {
                      console.warn('No matching PlaidItem found for account:', account);
                      return null;
                    }

                    const isDisconnecting = disconnectingAccounts[account.id];
                    // Find logoUrl from the imported mockPlaidItems
                    const logoUrl = plaidItem?.logoUrl; 

                    return (
                      <div
                        key={account.id}
                        className={`flex items-center justify-between p-2 border rounded-lg ${isDisconnecting ? 'opacity-50' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                            {plaidItem.logoUrl ? (
                              <img 
                                src={plaidItem.logoUrl} 
                                alt={`${plaidItem.institutionName} logo`} 
                                className="h-full w-full object-contain"
                              />
                            ) : (
                              <span className="text-xs font-medium">
                                {plaidItem?.institutionName?.charAt(0) || 'B'}
                              </span>
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium">{plaidItem?.institutionName || 'Bank'}</p>
                            <p className="text-xs text-muted-foreground">
                              {account.name} ••••{account.mask}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right">
                            <p className="text-sm font-medium">
                              {new Intl.NumberFormat('en-US', { 
                                style: 'currency', 
                                currency: account.currency || 'USD' 
                              }).format(account.balance || 0)}
                            </p>
                            {account.availableBalance !== null && account.availableBalance !== undefined && (
                              <p className="text-xs text-muted-foreground">
                                Available: {new Intl.NumberFormat('en-US', { 
                                  style: 'currency', 
                                  currency: account.currency || 'USD' 
                                }).format(account.availableBalance)}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDisconnect(plaidItem.plaidItemId, account.id)}
                              disabled={isDisconnecting}
                            >
                              {isDisconnecting ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Disconnecting...
                                </>
                              ) : (
                                'Disconnect'
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ),
      [CARD_IDS.BUDGET_PROGRESS]: (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle>Budget Progress</CardTitle> {/* Updated Title */} 
                <CardDescription>Progress for each budget category.</CardDescription> {/* Updated Desc */} 
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-9 w-9"
                onClick={() => onNavigate ? onNavigate('budget-overview' as any) : console.log('Navigate to Budgets/Settings')} 
              >
                <Settings className="h-5 w-5" />
                <span className="sr-only">Settings</span>
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {isBudgetsLoading ? (
                <Skeleton className="h-24 w-full" /> 
              ) : (
                // Use the new dashboardBudgetData array
                dashboardBudgetData.map((category) => { 
                  const progress = category.allocated > 0 ? Math.min(100, (category.spent / category.allocated) * 100) : 0; 
                  const isOverBudget = category.spent > category.allocated;
                  const remaining = category.allocated - category.spent;
                  
                  const indicatorClass = category.color
                    ? `[&>div]:bg-[${category.color}]`
                    : '[&>div]:bg-primary';

                  return (
                    <div key={category.id} className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{category.icon}</span>
                          <span className="text-sm font-medium">{category.name}</span>
                        </div>
                        <span className="text-xs font-medium text-muted-foreground">
                          {isOverBudget ? `${formatCurrency(Math.abs(remaining))} Over` : `${formatCurrency(remaining)} Left`}
                        </span>
                      </div>
                      <Progress
                        value={progress}
                        className={`h-2 ${indicatorClass}`}
                        aria-label={`${progress.toFixed(0)}% spent`}
                      />
                      <div className="text-xs text-muted-foreground flex justify-between">
                        <span>{formatCurrency(category.spent)}</span>
                        <span>{formatCurrency(category.allocated)}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
      ),
      [CARD_IDS.EARNED_BADGES]: (
        <EarnedBadgesCard badges={allEarnedBadges} isLoading={false} /> 
      ),
      [CARD_IDS.AI_CHAT]: (
        <AIChatCard />
      ),
      [CARD_IDS.RECENT_TRANSACTIONS]: (
        <RecentTransactionsCard 
          transactions={transactions} 
          isLoading={isTransactionsLoading} 
          onNavigate={onNavigate}
          userCategorySettings={userCategorySettings} 
          bankAccounts={bankAccounts}
        />
      ),
      [CARD_IDS.UPCOMING_TRANSACTIONS]: (
        <UpcomingTransactionsCard
          transactions={upcomingTransactions as any[]} // Cast for now, might need type adjustment
          isLoading={isUpcomingLoading}
          onNavigate={onNavigate} // Assuming same navigation behavior
          userCategorySettings={userCategorySettings}
          bankAccounts={bankAccounts}
          title="Upcoming Bills & Subscriptions" // Example title prop
          description="Recurring transactions expected soon." // Example description prop
        />
      ),
    };
  }, [
      bankAccounts, plaidItems, disconnectingAccounts, handleRefreshBalances, isRefreshing, // Bank Accounts deps
      onNavigate, isBudgetsLoading, dashboardBudgetData, // Budget Progress deps
      allEarnedBadges, // Earned Badges deps
      transactions, isTransactionsLoading, userCategorySettings, // Recent Tx deps (onNavigate, bankAccounts already listed)
      upcomingTransactions, isUpcomingLoading, // Upcoming Tx deps (onNavigate, userCategorySettings, bankAccounts already listed)
  ]);

  return (
    <>
      <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2 px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>Dashboard</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>   
          </Breadcrumb>
        </div>
      </header>
      
      <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
        {/* Grid for the top 4 summary cards - NOW DRAGGABLE */}
        <DndContext
          sensors={sensors}
          collisionDetection={rectIntersection} // Good for grids
          onDragEnd={handleTopCardDragEnd}
        >
          <SortableContext items={topCardOrder} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {topCardOrder.map((id) => {
                const CardComponent = cardComponents[id];
                if (!CardComponent) {
                  console.warn(`Dashboard: No component found for top card ID: ${id}`);
                  return null;
                }
                return (
                  <SortableItem key={id} id={id}>
                    {CardComponent}
                  </SortableItem>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>

        {/* Original top card rendering removed */}
        {/* <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"> ... </div> */}

        {/* Bank Accounts Card - THIS BLOCK WILL BE MOVED */}
        {/* <Card> ... </Card> */}

        {/* Render the 5th card (Total Transactions) separately - REMAINS HERE */}
        {summaryCards.length > 4 && (() => {
          const card = summaryCards[4];
          return (
            <div className="mt-4"> {/* Add margin top for spacing */}
              <Card key={4}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-bold">
                    {card.title}
                  </CardTitle>
                  {card.icon}
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{card.value}</div>
                  <p className={`text-xs ${card.trend === 'up' ? 'text-green-500' : card.trend === 'down' ? 'text-red-500' : 'text-muted-foreground'}`}>
                    {card.description}
                  </p>
                </CardContent>
              </Card>
            </div>
          );
        })()}
        
        {/* Wrap the main draggable content sections in DndContext */}
        <DndContext
          sensors={sensors}
          collisionDetection={rectIntersection} // Use rectIntersection for grid layouts
          onDragEnd={handleMainGridDragEnd} // Use the specific handler
        >
          {/* Use SortableContext for the items */}
          <SortableContext items={mainCardOrder} strategy={rectSortingStrategy}>
            {/* Grid for the draggable cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
              {mainCardOrder.map((id) => { // Use mainCardOrder state
                // Check if the component exists for the ID before rendering
                const CardComponent = cardComponents[id];
                if (!CardComponent) {
                    console.warn(`Dashboard: No component found for card ID: ${id}`);
                    return null; // Skip rendering if component not found
                }
                return (
                  <SortableItem key={id} id={id}>
                    {CardComponent}
                  </SortableItem>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </>
  );
} 